import {
  TaskProgressorRequestMode,
  type TaskProgressorCampaignDetails,
  type TaskProgressorContractSummary,
} from '@urbanx/agx.activities.client';
import {
  AgxButton,
  AgxCheckbox,
  AgxDropdown,
  AgxSpacer,
  AgxTypography,
  Icon_CheckmarkOutline,
  type DropdownOption,
} from '@urbanx/agx.ui-components2';
import Panel from 'components/panel/Panel';
import { getSelectedStageLabel } from './TaskProgressor.helpers';
import TaskProgressorContractDates from './TaskProgressorContractDates';

interface TaskProgressorWorkflowProps {
  campaignDetails: TaskProgressorCampaignDetails;
  selectedContract?: TaskProgressorContractSummary;
  contractOptions: DropdownOption[];
  stageOptions: DropdownOption[];
  selectedContractId?: string;
  selectedStage?: string;
  mode: TaskProgressorRequestMode;
  progressContract: boolean;
  canProgressContract: boolean;
  isReinitialiseOnly: boolean;
  isPending: boolean;
  isSaveDisabled: boolean;
  resultingCampaignStageLabel?: string;
  resultingContractStageLabel?: string;
  setMode: (mode: TaskProgressorRequestMode) => void;
  setSelectedContractId: (contractId: string) => void;
  setSelectedStage: (stage: string) => void;
  onProgressContractChange: (checked: boolean) => void;
  onSave: () => void;
}

const TaskProgressorWorkflow = ({
  campaignDetails,
  selectedContract,
  contractOptions,
  stageOptions,
  selectedContractId,
  selectedStage,
  mode,
  progressContract,
  canProgressContract,
  isReinitialiseOnly,
  isPending,
  isSaveDisabled,
  resultingCampaignStageLabel,
  resultingContractStageLabel,
  setMode,
  setSelectedContractId,
  setSelectedStage,
  onProgressContractChange,
  onSave,
}: TaskProgressorWorkflowProps) => (
  <AgxSpacer gap="spacing-xl" fill>
    <AgxSpacer
      direction="horizontal"
      gap="spacing-3xl"
      fill
      wrap
      contentAlignment="start"
      data-testid="task-progressor-workflow-layout"
    >
      <AgxSpacer
        gap="spacing-xl"
        data-testid="task-progressor-workflow-controls"
        style={{ flex: '62 1 0', minWidth: 'min(560px, 100%)' }}
      >
        <Panel title="Progress workflow">
          <AgxSpacer gap="spacing-2xl" fill>
            <AgxSpacer direction="horizontal" gap="spacing-3xl" fill wrap>
              <TaskProgressorDetail
                id="task-progressor-property"
                label="Property"
                value={campaignDetails.propertyAddress}
              />
              <TaskProgressorDetail
                id="task-progressor-campaign-stage"
                label="Campaign stage: current → resulting"
                value={`${campaignDetails.currentCampaignStageLabel} → ${resultingCampaignStageLabel ?? '-'}`}
              />
              <TaskProgressorDetail
                id="task-progressor-campaign-workflow-stage"
                label="Workflow stage to enter"
                value={
                  progressContract
                    ? 'Unchanged'
                    : getSelectedStageLabel(selectedStage, campaignDetails.availableStages)
                }
              />
            </AgxSpacer>

            <AgxSpacer gap="spacing-sm" fill>
              <AgxTypography id="task-progressor-mode-label" type="label" size="medium">
                Mode
              </AgxTypography>
              <AgxSpacer gap="spacing-md" style={{ width: '480px', maxWidth: '100%' }}>
                <AgxCheckbox
                  id="task-progressor-mode-change-stage"
                  type="radio"
                  style="row"
                  position="full"
                  fill
                  label="Change stage"
                  checked={mode === TaskProgressorRequestMode.ChangeStage}
                  disabled={isPending}
                  onChange={() => setMode(TaskProgressorRequestMode.ChangeStage)}
                />
                <AgxCheckbox
                  id="task-progressor-mode-reinitialise"
                  type="radio"
                  style="row"
                  position="full"
                  fill
                  label="Reinitialise tasks only"
                  checked={isReinitialiseOnly}
                  disabled={isPending}
                  onChange={() => setMode(TaskProgressorRequestMode.ReinitialiseTasksOnly)}
                />
              </AgxSpacer>
            </AgxSpacer>

            <AgxSpacer gap="spacing-md" fill>
              <AgxSpacer style={{ width: '480px', maxWidth: '100%' }}>
                <AgxCheckbox
                  id="task-progressor-progress-contract"
                  style="row"
                  position="full"
                  fill
                  label="Progress associated contract"
                  subLabel={canProgressContract ? undefined : 'No associated contract is available'}
                  checked={progressContract}
                  disabled={(!canProgressContract && !progressContract) || isPending}
                  onChange={onProgressContractChange}
                />
              </AgxSpacer>

              {progressContract && (
                <AgxSpacer style={{ width: '480px', maxWidth: '100%' }}>
                  <AgxDropdown
                    id="task-progressor-contract"
                    label="Contract"
                    options={contractOptions}
                    selected={selectedContractId}
                    onChange={setSelectedContractId}
                    placeholder="Select contract"
                    disabled={isPending}
                    stretch
                    hideOptionalText
                  />
                </AgxSpacer>
              )}

              <AgxSpacer direction="horizontal" gap="spacing-xl" contentAlignment="end" wrap>
                <AgxSpacer style={{ flex: '1 1 360px', maxWidth: '480px' }}>
                  <AgxDropdown
                    id="task-progressor-stage"
                    label="Workflow stage to enter"
                    options={stageOptions}
                    selected={selectedStage}
                    onChange={setSelectedStage}
                    placeholder="Select workflow stage"
                    disabled={isPending}
                    stretch
                    hideOptionalText
                  />
                </AgxSpacer>

                <AgxButton
                  id="task-progressor-save"
                  text={isPending ? 'Saving' : 'Save'}
                  variant="hollow"
                  leftIcon={<Icon_CheckmarkOutline width={16} height={16} />}
                  disabled={isSaveDisabled}
                  onClick={onSave}
                />
              </AgxSpacer>
            </AgxSpacer>
          </AgxSpacer>
        </Panel>
      </AgxSpacer>

      {selectedContract && (
        <AgxSpacer
          data-testid="task-progressor-contract-dates-column"
          style={{ flex: '38 1 0', minWidth: 'min(360px, 100%)' }}
        >
          <Panel title="Contract details">
            <AgxSpacer gap="spacing-xl" fill>
              <AgxSpacer direction="horizontal" gap="spacing-3xl" fill wrap>
                <TaskProgressorDetail
                  id="task-progressor-contract-stage"
                  label="Contract stage: current → resulting"
                  value={`${selectedContract.currentStageLabel} → ${resultingContractStageLabel ?? '-'}`}
                />
                <TaskProgressorDetail
                  id="task-progressor-contract-workflow-stage"
                  label="Workflow stage to enter"
                  value={getSelectedStageLabel(
                    progressContract ? selectedStage : undefined,
                    selectedContract.availableStages
                  )}
                />
              </AgxSpacer>
              <TaskProgressorContractDates contractDates={selectedContract.contractDates} />
            </AgxSpacer>
          </Panel>
        </AgxSpacer>
      )}
    </AgxSpacer>
  </AgxSpacer>
);

const TaskProgressorDetail = ({
  id,
  label,
  value,
}: {
  id: string;
  label: string;
  value?: string;
}) => {
  if (!value) return null;

  return (
    <AgxSpacer gap="spacing-xs">
      <AgxTypography id={`${id}-label`} type="label" size="small" color="var(--text-secondary)">
        {label}
      </AgxTypography>
      <AgxTypography id={`${id}-value`} type="body_ui_strong" size="medium">
        {value}
      </AgxTypography>
    </AgxSpacer>
  );
};

export default TaskProgressorWorkflow;
